import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { contentAPI } from '@/utils/api';
import { gsap } from 'gsap';
import { Loader } from '@/components/ui/loader';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export const Industries = () => {
  const { t, i18n } = useTranslation();
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIndustries();
  }, []);

  const fetchIndustries = async () => {
    try {
      const res = await contentAPI.getIndustries();
      setIndustries(res.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching industries:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!loading) {
      // clear any previous transforms
      ScrollTrigger.getAll().forEach(t => t.kill());
      gsap.set('.industry-card', { clearProps: 'transform,opacity' });

      gsap.from('.industry-card', {
        scrollTrigger: {
          trigger: '.industries-grid',
          start: 'top 70%'
        },
        opacity: 0,
        y: 50,
        duration: 0.8,
        stagger: 0.15
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [loading]);

  const getLangContent = (content) => {
    return content?.[i18n.language] || content?.en || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-primary">
        <Loader className="text-secondary" text={t('common.loading')} />
      </div>
    );
  }

  return (
    <div className="bg-primary min-h-screen">
      {/* Hero Section */}
      <section
        data-testid="industries-hero"
        className="relative py-32 flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(11, 28, 45, 0.85), rgba(11, 28, 45, 0.85)), url('https://images.unsplash.com/photo-1525226821587-9412a5c8e7e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBza3lzY3JhcGVyJTIwYmx1ZSUyMGdvbGR8ZW58MHx8fHwxNzcyMjY5ODc4fDA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
          <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4">
            Industries We Serve
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Specialized recovery solutions across diverse industry sectors
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section data-testid="industries-grid" className="py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="industries-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
            {industries.map((industry) => (
              <div
                key={industry.slug}
                data-testid={`industry-card-${industry.slug}`}
                className="industry-card group flex flex-col justify-between bg-[#111827] border border-white/5 hover:border-secondary/50 p-8 rounded-sm card-hover h-full"
              >
                <h3 className="font-heading text-2xl font-medium text-white mb-3 group-hover:text-secondary transition-colors">
                  {getLangContent(industry.name)}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {getLangContent(industry.description)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
